import {
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  SystemClassifierItemDto,
  TextEllipsis,
  TextLink,
  Padding,
  formatDateTimeShort,
  useSystemClassifierItemsBy,
  BusinessLogDto,
  BusinessLogItem,
  AlgorithmFileUrl,
  Detail,
} from '@atlas-frontend/sdk';
import { Button, Flex } from 'antd';
import { FC, memo, useState } from 'react';
import translations from './BusinessLogDataCell.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { expand } from 'uri-template-lite';

const { addTranslations, useTranslation } = i18nNsFactory(
  'business-log-data-cell',
);
addTranslations(translations);

export interface BusinessLogDataCellProps {
  data: BusinessLogDto | null;
  keys: Map<string, SystemClassifierItemDto>;
  logDataKey: 'logData' | 'logData2';
  onTraceIdClick?: (traceId: string) => void;
}

const actionKeySequence = (action: string): Record<string, number> => {
  // Keys for some actions are not sorted according to the system classifier sequence no
  switch (action) {
    case 'CONFIDENTIAL_CHANGE':
      return {
        input: 1,
        intermediateOutput: 2,
        output: 3,
        report: 4,
        inputGroup: 5,
        confidential: 6,
      };
    case 'DATA_OBJ_STAT_CHG':
      return {
        input: 1,
        intermediateOutput: 2,
        output: 3,
        report: 4,
        version: 5,
        stateOld: 6,
        stateNew: 7,
      };
    case 'DATA_UPLOAD':
      return {
        fileName: 1,
        scope: 2,
        inputGroup: 3,
        input: 4,
        valueDate: 5,
        processingTime: 6,
        status: 7,
        loadingEndTime: 8,
      };
    case 'MAPPING_ADD':
    case 'MAPPING_MODIFY':
    case 'MAPPING_REMOVE':
      return {
        classifierMapping: 1,
        classifierMappingOld: 2,
        classifierMappingNew: 3,
        mappingClassifier1: 4,
        mappingClassifier2: 5,
        categoryMappingsAdded: 6,
        categoryMappingsModified: 7,
        categoryMappingsRemoved: 8,
      };
    case 'SAMPLE_LOAD':
      return {
        inputGroup: 1,
        input: 2,
        valueDate: 3,
        processingTime: 4,
        status: 5,
        loadingEndTime: 6,
      };
    case 'SUBJECTS_LOAD':
      return {
        dataSubject: 1,
        processingTime: 2,
        status: 3,
        loadingEndTime: 4,
      };
    case 'ALGORITHM_MODIFY':
      return {
        initialFileName: 1,
        uploadedFileName: 2,
        scope: 3,
        input: 4,
        algorithmsAdded: 5,
        algorithmsModified: 6,
        draftToActive: 7,
        activeToDraft: 8,
        activeToInactive: 9,
        inactiveToActive: 10,
        algorithmsRemoved: 11,
      };
  }

  return {};
};

export const BusinessLogDataCell: FC<BusinessLogDataCellProps> = memo(
  ({ data, keys, logDataKey, onTraceIdClick }: BusinessLogDataCellProps) => {
    const [isClipped, setIsClipped] = useState(false);
    const [clippedContentModalOpen, setClippedContentModalOpen] =
      useState(false);
    const { t } = useTranslation();

    if (!data || !('action' in data)) {
      return null;
    }

    const { action, traceId, id } = data;
    const logData = data[logDataKey];
    if (!logData || logData.length === 0) {
      return null;
    }

    const groupedLogData = sortAndGroup(action, logData, keys);
    const groupCount = groupedLogData.length;

    const content = (
      <>
        {groupedLogData.map((group, index) => (
          <div
            key={`group-${index}`}
            style={index < groupCount - 1 ? { marginBottom: '1rem' } : {}}
          >
            {group.map(({ key, name, value }, index) => (
              <div key={index}>
                <strong>
                  {`${name}${value.count ? ' (' + value.count + ')' : ''}`}
                  {value.value ? ': ' : ''}
                </strong>
                <ItemValue id={id} itemKey={key} value={value.value} />
              </div>
            ))}
          </div>
        ))}
        {traceId && logDataKey === 'logData' && (
          <Detail label={t('traceId')}>
            <TextLink
              id={id.toString()}
              onClick={() => {
                setClippedContentModalOpen(false);
                onTraceIdClick?.(traceId);
              }}
            >
              {traceId}
            </TextLink>
          </Detail>
        )}
      </>
    );

    return (
      <Flex vertical>
        <TextEllipsis rows={7} onIsClipped={setIsClipped} renderTooltip={false}>
          {content}
        </TextEllipsis>
        {isClipped && (
          <TextLink
            id={`${id}-${logDataKey}-read-more`}
            onClick={() => {
              setClippedContentModalOpen(true);
            }}
          >
            {t('modal.readMore')}
          </TextLink>
        )}
        <ClippedContentModal
          open={clippedContentModalOpen}
          onClose={() => setClippedContentModalOpen(false)}
          data={data}
          content={content}
        />
      </Flex>
    );
  },
);

const ClippedContentModal: FC<{
  open: boolean;
  onClose: () => void;
  data: BusinessLogDto;
  content: React.ReactNode;
}> = ({ open, onClose, data, content }) => {
  const { t } = useTranslation();
  const { action } = data;
  const { BUSINESS_LOG_ACTION: logActions } = useSystemClassifierItemsBy(
    ['BUSINESS_LOG_ACTION'],
    'code',
  );

  return (
    <Modal
      open={open}
      maskClosable={false}
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <Button id="close-modal" type="primary" onClick={onClose}>
              {t('modal.close')}
            </Button>
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      width="50rem"
    >
      <ModalHeader>
        <ModalTitle>{t('modal.title')}</ModalTitle>
      </ModalHeader>
      <Padding scrollable>
        <div style={{ marginBottom: '1rem' }}>
          <h5>{t('modal.mainData')}</h5>
          <Detail label={t('modal.createdAt')}>
            {formatDateTimeShort(data.createdAt) ?? ''}
          </Detail>
          <Detail label={t('modal.createdBy')}>
            {data?.createdBy?.username}
          </Detail>
          <Detail label={t('modal.action')}>
            {logActions.get(action)?.name ?? ''}
          </Detail>
        </div>
        <h5>{t('modal.content')}</h5>
        {content}
      </Padding>
    </Modal>
  );
};

function getSequence(
  action: string,
  id: number,
  classifier?: SystemClassifierItemDto,
) {
  if (action === 'OUTPUT_CONFIRM') {
    return id;
  }

  return classifier?.sequenceNumber;
}

function sortAndGroup(
  action: string,
  logData: BusinessLogItem[],
  keys: Map<string, SystemClassifierItemDto>,
) {
  const keySequence = actionKeySequence(action);
  const sequenced = logData.map(({ id, key, value, groupNr }) => {
    const classifier = keys.get(key);
    return {
      key,
      name: classifier?.name ?? key,
      sequenceNumber:
        keySequence[key] ??
        getSequence(action, id, classifier) ??
        Number.MAX_SAFE_INTEGER,
      value,
      groupNr,
    };
  });

  const grouped = sequenced.reduce(
    (acc, item) => {
      const groupKey = item.groupNr ?? 'default';
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
      return acc;
    },
    {} as Record<string, typeof sequenced>,
  );

  Object.values(grouped).forEach((group) =>
    group.sort((a, b) => a.sequenceNumber - b.sequenceNumber),
  );

  const sortedGrouped = [
    ...(grouped['default'] ? [grouped['default']] : []),
    ...Object.entries(grouped)
      .filter(([groupNr]) => Number(groupNr) > 0)
      .toSorted()
      .map(([, group]) => group),
  ];

  return sortedGrouped;
}

const ItemValue: FC<{
  id: number;
  itemKey: string;
  value: string | null;
}> = ({ id, itemKey: key, value }) => {
  if (!key || !value) return null;

  if (
    value.startsWith('LINK:') &&
    (key === 'initialFileName' || key === 'uploadedFileName')
  ) {
    const [name, s3Path] = value.replace('LINK:', '').split('|');
    return (
      <a
        id={`${key}-${id}-download`}
        download
        href={expand(AlgorithmFileUrl, {
          s3Path,
          fileName: name,
        })}
      >
        {name}
      </a>
    );
  } else if (value.startsWith('LINK:')) {
    const [name, url] = value.replace('LINK:', '').split('|');
    return (
      <TextLink id={`${key}-${id}-link`} href={url}>
        {name}
      </TextLink>
    );
  }

  return <span>{value}</span>;
};
